import { BehaviorSubject, Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { AlcmeonContextService } from 'src/app/core/alcmeon-context/alcmeon-context.service';
import { AttachmentType, BlobAndFileName, Editor2Attachment } from 'src/app/shared/models/misc.model';
import { BasicService } from 'src/app/shared/services/basic.service';
import { UploadService } from 'src/app/shared/services/upload.service';
import { BZCSplittableRichLink } from 'src/app/task-process/shared/models/bzc-splittable-rich-link.model';
import { environment } from 'src/environments/environment';

import { ActionButtonConfig } from '../../action-button-config';
import { EditorConfig } from '../../editor-config';
import { AgentShortcut } from '../agent-shortcuts-modal/agent-shortcut.model';
import { AgentShortcutsModalComponent } from '../agent-shortcuts-modal/agent-shortcuts-modal.component';
import { GifSearchModalComponent } from '../gif-search-modal/gif-search-modal.component';
import { BZCRichLinkModalComponent } from './bzc-richlink-modal/bzc-richlink-modal.component';
import { SingleMessages } from './single-messages';
import { UploadByChunkHelper } from './upload-by-chunk-helper';

const ATTACHMENT_TYPE_ATTRIBUTE = 'data-attachment-type';

export class Attachments {
  uploadProgress$ = new BehaviorSubject<number>(0);
  uploading$ = new BehaviorSubject<boolean>(false);
  buttons$ = new BehaviorSubject<ActionButtonConfig[]>([]);
  uploadByChunkHelper: UploadByChunkHelper;

  private attachments: Editor2Attachment[] = [];

  private fileInput: HTMLInputElement;

  private editorConfig: EditorConfig;
  private siteId: number;
  private forceHideAttachments: boolean;

  /**
   * Called by answer editor when the component is inited
   * or when a new single message is requested by the user
   */
  constructor(
    private messagesHandler: SingleMessages,
    private basicService: BasicService,
    private uploadService: UploadService,
    private alcmeonContext: AlcmeonContextService
  ) {
    this.editorConfig = this.messagesHandler.editorConfig;
    this.siteId = this.messagesHandler.siteId;
    this.updateActionSheetButtons();
    this.uploadByChunkHelper = new UploadByChunkHelper(
      this.uploading$,
      this.uploadProgress$,
      this.uploadService,
      this.alcmeonContext,
      this.basicService
    );
    this.uploading$.subscribe(() => this.messagesHandler.updateValidity());

    this.handleBZCSplittableRichLinkSubscription();
  }

  /**
   * Called by SingleMessageEditor when the view is ready
   * (the file input element ref is only available in afterViewInit)
   */
  setFileInput(fileInput) {
    this.fileInput = fileInput;
  }

  getAttachments() {
    return this.attachments;
  }

  hasAttachment() {
    return this.attachments.length > 0;
  }

  hasAgentShortcut() {
    return this.attachments.some(attachment => attachment.type === AttachmentType.AgentShortcut);
  }

  clearAttachments() {
    this.attachments = [];
    this.updateActionSheetButtons();
    this.messagesHandler.updateValidity();
  }

  addPictureAttachment(pictureId, mimeType?) {
    this.addAttachment(AttachmentType.Picture, { pictureId }, mimeType);
  }

  addVideoAttachment(videoId) {
    this.addAttachment(AttachmentType.Video, { videoId });
    this.uploading$.next(false);
  }

  addFileAttachment(fileId, fileName) {
    this.addAttachment(AttachmentType.File, { fileId, fileName });
  }

  addBzcRichLinkAttachment(value) {
    this.addAttachment(AttachmentType.BZCRichLink, value);
  }

  addAppleSplittableRichLinkAttachment(value) {
    this.addAttachment(AttachmentType.BZCSplittableRichLink, value);
  }

  addAgentShortcut(value: AgentShortcut) {
    this.addAttachment(AttachmentType.AgentShortcut, value);
  }

  removeAttachment(attachment) {
    this.attachments.splice(this.attachments.indexOf(attachment), 1);
    this.updateActionSheetButtons();
    this.messagesHandler.updateValidity();

    if (attachment.type === AttachmentType.Video) {
      this.uploadByChunkHelper.removeFileFromVideoId(attachment.value.videoId);
    }
  }

  setForceHideAttachments(forceHideAttachments: boolean) {
    this.forceHideAttachments = forceHideAttachments;
    this.updateActionSheetButtons();
  }

  // cordova (for video only) and browser
  onFileSelected(files: FileList) {
    const file = files.item(0);
    const type = this.fileInput.getAttribute(ATTACHMENT_TYPE_ATTRIBUTE);
    switch (type) {
      case AttachmentType.Picture:
        this.uploadAndAddPictureAttachment(file, file.type);
        break;
      case AttachmentType.File:
        this.attachFileFromBlob({
          blob: file,
          fileName: file.name,
        });
        break;
    }
  }

  private handleBZCSplittableRichLinkSubscription() {
    this.basicService.handleBZCSplittableRichLinkSubscription?.unsubscribe();
    this.basicService.handleBZCSplittableRichLinkBS = new BehaviorSubject<BZCSplittableRichLink>(undefined);
    this.basicService.handleBZCSplittableRichLinkSubscription = new Subscription();
    this.basicService.handleBZCSplittableRichLinkSubscription.add(
      this.basicService.handleBZCSplittableRichLinkBS.subscribe(result => {
        if (result) {
          this.addAppleSplittableRichLinkAttachment(result);
        }
      })
    );

    this.basicService.handleBZCSplittableRichLinkSubscription.add(
      this.basicService.handleClearBZCSplittableRichLinkAttachmentsBS.subscribe(result => {
        if (result) {
          this.attachments = this.attachments.filter(({ type }) => type !== AttachmentType.BZCSplittableRichLink);
          this.updateActionSheetButtons();
          this.messagesHandler.updateValidity();
        }
      })
    );
  }

  private addAttachment(type, value, mimeType?) {
    this.attachments.push({ type, value, mimeType });
    this.updateActionSheetButtons();
    this.messagesHandler.updateValidity();
  }

  private updateActionSheetButtons() {
    const buttons = [];
    if (this.shouldShowPictureButton()) {
      if (environment.isNativeApp) {
        buttons.push(
          new ActionButtonConfig('m/task-process/attach/photo-library', 'image', () => {
            this.onAttachPhotoClicked();
          })
        );
      } else {
        buttons.push(
          new ActionButtonConfig('m/task-process/attach/picture', 'image', () => {
            this.onAttachPhotoClicked();
          })
        );
      }
    }
    if (this.shouldShowGifSearchButton()) {
      const config = new ActionButtonConfig('m/task-process/attach/gif', null, () => {
        this.onAttachGifClicked();
      });
      config.iconUrl = 'assets/img/gif-icon.svg';
      buttons.push(config);
    }
    if (this.shouldShowVideoButton()) {
      buttons.push(
        new ActionButtonConfig('m/task-process/attach/video', 'videocam', () => {
          this.onAttachVideoClicked();
        })
      );
    }
    if (this.shouldShowRichLinkButton()) {
      buttons.push(
        new ActionButtonConfig('m/task-process/attach/richlink', 'link-outline', () => {
          this.onAttachRichLinkClicked();
        })
      );
    }
    if (this.shouldShowFileButton()) {
      buttons.push(
        new ActionButtonConfig('m/task-process/attach/file', 'attach', () => {
          this.onAttachFileClicked();
        })
      );
    }
    if (this.shouldShowAgentShortcutButton()) {
      buttons.push(
        new ActionButtonConfig(
          'm/task-process/attach/chatbot-message',
          'chatbubbles-outline',
          this.onAgentShortcutsClicked
        )
      );
    }
    this.buttons$.next(buttons);
  }

  private onAgentShortcutsClicked = async () => {
    const modal = await this.basicService.cardModal(AgentShortcutsModalComponent, {
      siteId: this.siteId,
    });
    modal.onDidDismiss().then(result => {
      if (result?.data) {
        this.addAttachment(AttachmentType.AgentShortcut, result.data);
      }
    });
  };

  private shouldShowPictureButton() {
    return (
      !this.forceHideAttachments &&
      this.editorConfig.offersPictureAttachment &&
      this.numberOfAttachmentOfType(AttachmentType.Picture) < this.editorConfig.maxImageNumber &&
      this.numberOfAttachmentOfType(AttachmentType.Picture, this.editorConfig.gifMimeType) <
        this.editorConfig.maxGifNumber &&
      !(!this.editorConfig.canMixAttachmentTypes && this.hasAttachmentOtherThanType(AttachmentType.Picture))
    );
  }

  private shouldShowGifSearchButton() {
    return (
      !this.forceHideAttachments &&
      this.shouldShowPictureButton() &&
      this.editorConfig.offersGiphySearch &&
      this.numberOfAttachmentOfType(AttachmentType.Picture, this.editorConfig.gifMimeType) <
        this.editorConfig.maxGifNumber &&
      !(
        !this.editorConfig.canMixAttachmentTypes &&
        this.hasAttachmentOtherThanType(AttachmentType.Picture, this.editorConfig.gifMimeType)
      )
    );
  }

  private shouldShowVideoButton() {
    return (
      !this.forceHideAttachments &&
      this.editorConfig.offersVideoAttachment &&
      !this.hasAttachmentOfType(AttachmentType.Video) &&
      !(!this.editorConfig.canMixAttachmentTypes && this.hasAttachmentOtherThanType(AttachmentType.Video))
    );
  }

  private shouldShowRichLinkButton() {
    return (
      !this.forceHideAttachments &&
      this.editorConfig.offersRichLinkAttachment &&
      !this.hasAttachmentOfType(AttachmentType.BZCRichLink) &&
      !(!this.editorConfig.canMixAttachmentTypes && this.hasAttachmentOtherThanType(AttachmentType.BZCRichLink))
    );
  }

  private shouldShowFileButton() {
    return (
      !this.forceHideAttachments &&
      this.editorConfig.offersFileAttachment &&
      !this.hasAttachmentOfType(AttachmentType.File) &&
      !(!this.editorConfig.canMixAttachmentTypes && this.hasAttachmentOtherThanType(AttachmentType.File))
    );
  }

  private shouldShowAgentShortcutButton() {
    return (
      !this.forceHideAttachments &&
      this.editorConfig.offersAgentShortcutAttachment &&
      !this.hasAttachmentOfType(AttachmentType.AgentShortcut)
    );
  }

  private hasAttachmentOfType(type, mimeType?) {
    if (mimeType) {
      return this.attachments.some(attachement => attachement.type === type && attachement.mimeType === mimeType);
    } else {
      return this.attachments.some(attachement => attachement.type === type);
    }
  }

  private hasAttachmentOtherThanType(type, mimeType?) {
    if (mimeType) {
      return this.attachments.some(attachement => attachement.type !== type || attachement.mimeType !== mimeType);
    } else {
      return this.attachments.some(attachement => attachement.type !== type);
    }
  }

  private numberOfAttachmentOfType(type, mimeType?) {
    if (mimeType) {
      return this.attachments.filter(attachement => attachement.type === type && attachement.mimeType === mimeType)
        .length;
    } else {
      return this.attachments.filter(attachement => attachement.type === type).length;
    }
  }

  private getVideoAttachmentMaxSize() {
    return this.editorConfig.videoMaxSizeMb;
  }

  private getVideoAttachmentSupportedTypes() {
    return this.editorConfig.videoMimeTypes;
  }

  private getPictureAttachmentMaxSize() {
    return this.editorConfig.pictureMaxSizeMb;
  }

  private getPictureAttachmentSupportedTypes() {
    return this.editorConfig.imageMimeTypes;
  }

  private getFileAttachmentMaxSize() {
    return this.editorConfig.fileMaxSizeMb;
  }

  private getFileAttachmentSupportedTypes() {
    return this.editorConfig.fileMimeTypes;
  }

  private async onAttachRichLinkClicked() {
    const modalInput = {
      siteId: this.siteId,
    };
    const modal = await this.basicService.cardModal(BZCRichLinkModalComponent, modalInput);
    const { data } = await modal.onWillDismiss();
    if (data) {
      this.addBzcRichLinkAttachment(data);
    }
  }

  // both cordova and browser
  private onAttachPhotoClicked() {
    if (this.editorConfig.canMixAttachmentTypes) {
      this.triggerFileDialog(AttachmentType.Picture, this.editorConfig.imageMimeTypes);
    } else {
      if (this.hasAttachmentOfType(AttachmentType.Picture)) {
        if (this.hasAttachmentOfType(AttachmentType.Picture, this.editorConfig.gifMimeType)) {
          this.triggerFileDialog(AttachmentType.Picture, this.editorConfig.gifMimeType);
        } else {
          this.triggerFileDialog(AttachmentType.Picture, this.editorConfig.imageButNotGifMimeTypes);
        }
      } else {
        this.triggerFileDialog(AttachmentType.Picture, this.editorConfig.imageMimeTypes);
      }
    }
  }

  private async onAttachGifClicked() {
    const modal = await this.basicService.cardModal(GifSearchModalComponent);
    modal.onDidDismiss().then(result => {
      if (result.data) {
        // Get the image as blob and then upload it
        this.uploadService.convertGifToBlob(result.data.images.downsized_medium.url).subscribe(res => {
          this.uploadAndAddPictureAttachment(res, this.editorConfig.gifMimeType);
        });
      }
    });
  }

  // both cordova and browser
  private uploadAndAddPictureAttachment(blob: Blob, mimeType?: string) {
    const { size, type } = blob;

    let translationPath;
    let translationData;

    const checkWeight = weight => {
      if (weight) {
        const maxWeight = (weight || 0) * 1024 * 1024;
        if (maxWeight && size > maxWeight) {
          translationPath = 'media/upload/too-big/limitation';
          translationData = { max_size: weight };
          throw new Error();
        }
      }
    };

    const checkSupportedTypes = supportedTypes => {
      if (supportedTypes === 'image/*' && type.startsWith('image/')) {
        return;
      }
      const supportedContentTypes = supportedTypes?.split(',');

      if (supportedContentTypes?.length && !supportedContentTypes.includes(type)) {
        translationPath = 'media/upload/wrong-format/limitation';
        translationData = { supported_types: supportedTypes };
        throw new Error();
      }
    };

    try {
      checkSupportedTypes(this.getPictureAttachmentSupportedTypes());
      checkWeight(this.getPictureAttachmentMaxSize());
    } catch {
      this.basicService.presentAlert({
        subHeader: `${this.basicService.translate(translationPath, translationData)}`,
        buttons: [this.basicService.translate('m/ok')],
      });
      return;
    }

    this.uploading$.next(true);
    this.uploadProgress$.next(0);
    this.uploadService
      .uploadMedia(
        blob,
        AttachmentType.Picture,
        this.siteId,
        this.uploadProgress$,
        this.editorConfig.pollMediaUploadedOnSocialNetwork
      )
      .pipe(finalize(() => this.uploading$.next(false)))
      .subscribe(mediaId => this.addPictureAttachment(mediaId, mimeType));
  }

  // both cordova and browser
  private onAttachFileClicked() {
    this.triggerFileDialog(AttachmentType.File, this.editorConfig.fileMimeTypes);
  }

  private onAttachVideoClicked() {
    this.uploadByChunkHelper.init(this.getVideoAttachmentMaxSize(), this.getVideoAttachmentSupportedTypes());
    this.uploadByChunkHelper.uploadVideo().subscribe(videoId => this.videoUploaded(videoId));
  }

  private videoUploaded(videoId) {
    if (videoId) {
      this.addVideoAttachment(videoId);
    }
  }

  // both cordova (for video only) and browser
  private triggerFileDialog(attachmentType, accept) {
    this.fileInput.setAttribute(ATTACHMENT_TYPE_ATTRIBUTE, attachmentType);
    this.fileInput.setAttribute('accept', accept);
    this.fileInput.value = ''; // allow selecting the same file again
    this.fileInput.click();
  }

  // both cordova and browser
  private attachFileFromBlob(blobAndFileName: BlobAndFileName) {
    const { size, type } = blobAndFileName.blob;

    let translationPath;
    let translationData;

    const checkWeight = weight => {
      if (weight) {
        const maxWeight = (weight || 0) * 1024 * 1024;
        if (maxWeight && size > maxWeight) {
          translationPath = 'media/upload/too-big/limitation';
          translationData = { max_size: weight };
          throw new Error();
        }
      }
    };

    const checkSupportedTypes = supportedTypes => {
      const supportedContentTypes = supportedTypes?.split(',');
      if (supportedContentTypes?.length && !supportedContentTypes.includes(type)) {
        translationPath = 'media/upload/wrong-format/limitation';
        translationData = { supported_types: supportedTypes };
        throw new Error();
      }
    };

    try {
      checkSupportedTypes(this.getFileAttachmentSupportedTypes());
      checkWeight(this.getFileAttachmentMaxSize());
    } catch {
      this.basicService.presentAlert({
        subHeader: `${this.basicService.translate(translationPath, translationData)}`,
        buttons: [this.basicService.translate('m/ok')],
      });
      return;
    }

    this.uploading$.next(true);
    this.uploadProgress$.next(0);
    this.uploadService
      .uploadFile(
        blobAndFileName,
        this.siteId,
        this.uploadProgress$,
        this.editorConfig.pollMediaUploadedOnSocialNetwork
      )
      .pipe(finalize(() => this.uploading$.next(false)))
      .subscribe(fileId => this.addFileAttachment(fileId, blobAndFileName.fileName));
  }
}
